import { ROLE } from '@/enums/person';

import type {
	Movie,
	MovieContent,
	MovieOrShowContent,
	NewTitlesEdge,
	Season,
	ShowContent,
} from '@/@types/graphql-types';
import { ClipProvider, MonetizationType, PresentationType, ProfileType } from '@/@types/graphql-types';
import { CreditRole, ObjectType } from '@/@types/graphql-types';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { ArticleFragment, AuthorDataFragment } from '@/features/editorial/graphql/queries/GetArticleByUrl.query';
import { Genre } from '@/constants/types';
import { createThumborFilter } from '@/filters/thumbor';
import { getArticleBackdrop } from '@/features/editorial/helper';
import { AssertByTypename } from '@/helpers/graphql-model-helper';
import { getScorings } from '@/helpers/scoring-helper';
import { Country } from '@/interfaces/locale-details';
import { GetPopularTitlesQuery } from '@/pages/graphql/queries/GetPopularTitles.query';
import { useBuyBoxData } from '@/features/buybox/composables/useBuyboxData';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { UpcomingRelease } from '@/features/buybox/types/types';
import { ProfileNode } from '@/features/user-profile/types';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useI18n } from './composables/useI18n';
import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';
// import { useStreamingOffersHistory } from '@/features/streaming-offers-history/composables/useStreamingOffersHistory';

const PROTOCOL = 'https://';
const SUBDOMAIN = (JW_CONFIG.DOMAIN as string)?.startsWith('appdev') ? '' : 'www.';
const APP_URL = `${PROTOCOL}${SUBDOMAIN}${JW_CONFIG.DOMAIN}`;

function _getJsonLdContent(
	title: TitleDetail,
	genresByShortName: Record<string, Genre>,
	thumborFilter: ReturnType<typeof createThumborFilter>
) {
	if (title?.content == null) return {};

	const { tc } = useI18n();
	const userStore = useUserStore();

	const { isInternationalOffersTitle } = useInternationalOffersStatus({
		title: () => title,
	});

	const isMovie = title.__typename === 'Movie';
	const isShow = title.__typename === 'Show';
	const isSeason = title.__typename === 'Season';

	const { jwRating, imdbScore, imdbVotes } = title.content?.scoring ?? {};
	const jwScore = Math.round((jwRating ?? 0) * 100);
	const jwVotes = title.content?.interactions?.votesNumber ?? imdbVotes ?? 0;

	const getScore = (ratingSource: 'JW' | 'IMDB') => {
		if (ratingSource === 'JW') {
			return jwScore <= 0 ? '0' : `${jwScore}`;
		} else if (ratingSource === 'IMDB') {
			return imdbScore ?? 0;
		}
	};

	// Based on Google guidelines, `ratingCount` must be > 0.
	const getVotes = (ratingSource: 'JW' | 'IMDB') => {
		if (ratingSource === 'JW') {
			return Math.max(0, jwVotes);
		}

		if (ratingSource === 'IMDB') {
			return imdbVotes ?? 0;
		}

		return 0;
	};

	const titleContent = {} as any;
	const ratingCount = getVotes('JW');

	if (ratingCount >= 1 && title.content.scoring.jwRating != null && title.content.interactions?.votesNumber != null) {
		titleContent.aggregateRating = {
			'@type': 'AggregateRating',
			bestRating: '100',
			ratingCount: ratingCount.toString(),
			ratingValue: getScore('JW'),
			ratingExplanation:
				'The JustWatch rating is calculated by weighing and measuring how our users interact with shows & movies across different time periods and countries.',
		};
	}

	if (isMovie || isShow) {
		titleContent.name = title.content.title;
		titleContent.contentRating = title.content.ageCertification;

		// alternate name
		if (title.content.originalTitle !== title.content.title) {
			titleContent.alternateName = title.content.originalTitle;
		}

		if (isMovie) {
			titleContent.duration = getDurationString(title.content.runtime ?? 0);
		}

		if (isShow) {
			titleContent.containsSeason = getSeasons(title);
			titleContent.numberOfSeasons = title.totalSeasonCount;
		}
	}

	if (isSeason) {
		titleContent.name = `${title.show?.content.title || ''} ${title.content.title}`;
		titleContent.seasonNumber = title.content.seasonNumber;
		titleContent.numberOfEpisodes = title.totalEpisodeCount;
		titleContent.contentRating = title.show.content.ageCertification;

		titleContent.partOfSeries = {
			'@type': 'TVSeries',
			name: title.show?.content.title,
			url: new URL(title.show?.content.fullPath || '', APP_URL).toString(),
		};

		titleContent.episodes = title.episodes?.map(ep => ({
			'@type': 'TVEpisode',
			episodeNumber: ep.content.episodeNumber,
			name: ep.content.title,
			description: ep.content.shortDescription,
		}));
	}

	// offers
	const { allRegularOffers, upcomingReleases, dvdBluRayOffers } = useBuyBoxData({
		title: title,
		buyboxFilter: BuyBoxFilterOptions.ALL,
		buyboxUpcomingReleases: () => title.content.upcomingReleases,
	});
	const isJustWatchTV =
		title.justwatchTVOffers.length > 0 && title.justwatchTVOffers[0].package.technicalName === 'justwatchtv';

	const isJWTVAVOD = isJustWatchTV && title.justwatchTVOffers[0].monetizationType === MonetizationType.Free;

	const offerCount =
		getWatchActionsFromOffers(title, allRegularOffers.value).length +
		getWatchActionsFromDVDBluRayOffers(dvdBluRayOffers.value).length;

	const synopsis = title?.content?.editorialContent?.synopsis
		? title?.content?.editorialContent?.synopsis?.replace(/<[^>]*>/g, '').replace(/\n+/g, '. ')
		: title.content?.shortDescription ?? '';

	const actorList = getActorList(title);
	const directorList = getCreditList(title, ROLE.DIRECTOR);
	const authorList = getCreditList(title, ROLE.WRITER);
	const genreList = getGenresForTitle(genresByShortName, title);
	const linkedDataContent = {
		'@context': 'https://schema.org',
		'@id': new URL(title.content?.fullPath, APP_URL).toString(),
		'@type': getType(title.objectType),

		...(title.content.externalIds.wikidataId
			? { sameAs: `https://wikidata.org/wiki/${title.content.externalIds.wikidataId}` }
			: {}),

		...titleContent,

		dateCreated: title.content?.originalReleaseDate || title.content?.originalReleaseYear,
		description: synopsis,
		contentRating: titleContent.contentRating,
		image: title.content?.fullPosterUrl ? `${JW_CONFIG.IMAGESCALER_URL}${title.content?.fullPosterUrl}` : null,

		...(actorList.length > 0 && { actor: actorList }),
		...(authorList.length > 0 && { author: authorList }),
		...(directorList.length > 0 && { director: directorList }),
		...(genreList.length > 0 && { genre: genreList }),

		offers: {
			'@type': 'AggregateOffer',
			offerCount: offerCount,
			dateModified: title.maxOfferUpdatedAt,
		},
	};

	const textRecommendations = getTextRecommendations(title);
	if (textRecommendations.length > 0 && userStore.isLoggedIn) linkedDataContent.articles = textRecommendations;

	// Temporarily disable schema for wtw whilst disabled to logged out users
	// const reviews = getReviews(title);
	// if (reviews.length > 0) linkedDataContent.reviews = reviews;

	const productionCountries =
		(isSeason ? title.show.content.productionCountries : title.content?.productionCountries) ?? [];
	if (productionCountries.length > 0) {
		linkedDataContent.countryOfOrigin = (
			(isSeason ? title.show.content.productionCountries : title.content?.productionCountries) ?? []
		)
			.map(country => tc(`WEBAPP_COUNTRY_${country}`))
			.find(Boolean);
	}

	const dvdOffers = dvdBluRayOffers.value.filter(offer => offer.presentationType === PresentationType.Dvd);
	const blurayOffers = dvdBluRayOffers.value.filter(
		offer =>
			offer.presentationType === PresentationType.Bluray || offer.presentationType === PresentationType.Bluray_4K
	);

	const filteredDvdBluRayOffers = [...dvdOffers, ...blurayOffers];

	let watchActions = getWatchActionsFromOffers(title, allRegularOffers.value);
	const upcomingWatchActions = getWatchActionsFromUpcomingOffers(title, upcomingReleases.value);
	let dvdBluRayWatchActions = getWatchActionsFromDVDBluRayOffers(filteredDvdBluRayOffers);

	// TEMP: We limit the number of displayed offers in the schema if it's an international offers title, prevents exceeding the 2MB limit.
	if (isInternationalOffersTitle.value) {
		watchActions = watchActions.slice(0, 3);
		dvdBluRayWatchActions = getWatchActionsFromDVDBluRayOffers([
			...dvdBluRayOffers.value.filter(offer => offer.presentationType === PresentationType.Dvd).slice(0, 2),
			...dvdBluRayOffers.value
				.filter(
					offer =>
						offer.presentationType === PresentationType.Bluray ||
						offer.presentationType === PresentationType.Bluray_4K
				)
				.slice(0, 2),
		]);
	}

	if (watchActions.length || upcomingWatchActions.length || dvdBluRayWatchActions.length) {
		linkedDataContent.potentialAction = [...watchActions, ...upcomingWatchActions, ...dvdBluRayWatchActions];
	}

	const videoThumbnailFallback = 'https://www.justwatch.com/appassets/img/backdrop-placeholder.jpg';
	const trailers = getTrailers(title, thumborFilter);
	if (trailers.length > 0) {
		const trailer = trailers[0];
		linkedDataContent.trailer = {
			'@type': 'VideoObject',
			name: trailer.name,
			description: trailer.description,
			uploadDate: trailer.uploadDate,
			thumbnailUrl: trailer.thumbnailUrl || videoThumbnailFallback,
			contentUrl: trailer.contentUrl,
		};
	}

	// additionally we add all trailers in a list under `about`
	if (!isJWTVAVOD) {
		linkedDataContent.about = [
			{
				'@type': 'ItemList',
				itemListElement: trailers.map((trailer, idx) => ({
					'@type': 'ListItem',
					position: idx + 1,
					url: trailer.contentUrl,
					item: {
						'@type': 'VideoObject',
						name: trailer.name,
						description: trailer.description,
						uploadDate: trailer.uploadDate,
						thumbnailUrl: trailer.thumbnailUrl || videoThumbnailFallback,
						contentUrl: trailer.contentUrl,
					},
				})),
			},
		];
	}

	if (isJWTVAVOD) {
		const thumbnailUrl = trailers
			.filter(trailer => trailer.provider === ClipProvider.Dailymotion)
			.map(trailer => trailer.thumbnailUrl)
			.find(Boolean)
			?.replace('{format}', 'jpeg');
		const thumbnailUrlFallback = (title.content?.backdrops || [])
			.map(el => thumborFilter({ url: el.backdropUrl, imageType: 'backdrop' }))
			.find(Boolean)
			?.replace('{format}', 'jpeg');
		const videoSchemaObject: any = {
			'@context': 'https://schema.org',
			'@type': 'VideoObject',

			url: title.justwatchTVOffers[0].standardWebURL,
			name: title.content?.title,
			description: title.content?.shortDescription,
			thumbnailUrl: thumbnailUrl || thumbnailUrlFallback || videoThumbnailFallback,
			embedUrl: title.justwatchTVOffers[0].streamUrl,
			isAccessibleForFree: true,
		};

		// since we un-commented `dateCreated`, I'm not 100% sure if it's always filled or not.
		// looks like it is, but just to be sure.
		if (title.justwatchTVOffers[0].dateCreated) {
			videoSchemaObject.uploadDate = new Date(title.justwatchTVOffers[0].dateCreated).toISOString();
		}

		return [linkedDataContent, videoSchemaObject];
	}

	return linkedDataContent;
}

function getWatchActionsFromDVDBluRayOffers(offers: TitleOfferFragment[]) {
	return offers.map(offer => {
		const isLease = offer.monetizationType === MonetizationType.Rent;
		const actionType = isLease ? 'RentAction' : 'BuyAction';
		const businessFunction = isLease
			? 'http://purl.org/goodrelations/v1#LeaseOut'
			: 'http://purl.org/goodrelations/v1#Sell';
		const category = getVideoFormat(offer);

		return {
			'@type': actionType,
			target: {
				'@type': 'EntryPoint',
				urlTemplate: offer.standardWebURL,
			},
			expectsAcceptanceOf: {
				'@type': 'Offer',
				url: offer.standardWebURL,
				price: offer.retailPriceValue,
				priceCurrency: offer.currency,
				availability: 'http://schema.org/InStock',
				itemCondition: 'http://schema.org/NewCondition',
				businessFunction,
				category,
				seller: {
					'@type': 'Organization',
					name: offer.package.clearName,
				},
			},
		};
	});
}
function getType(type: ObjectType) {
	switch (type) {
		case ObjectType.Movie:
			return 'Movie';
		case ObjectType.Show:
			return 'TVSeries';
		case ObjectType.ShowSeason:
			return 'TVSeason';
		default:
			return null;
	}
}

function getAggregateRating(titleContent?: Pick<MovieContent, 'scoring' | 'externalIds'>) {
	if (titleContent == null) return {};

	const { tmdbScore, tmdbPopularity, imdbVotes } = titleContent.scoring;
	const [jwRating] = getScorings(titleContent.scoring, titleContent.externalIds);
	const ratingCount = imdbVotes ?? Number(jwRating.score ?? '0') ?? 0;

	if (tmdbScore == null || tmdbPopularity == null || ratingCount <= 0) return {};

	return {
		aggregateRating: {
			'@type': 'AggregateRating',
			bestRating: '100',
			// if there are no imdbVotes, make it at least 1 since `ratingCount` must be > 0.
			ratingCount: ratingCount.toString(),
			ratingValue: jwRating.score,
			ratingExplanation:
				'The JustWatch rating is calculated by weighing and measuring how our users interact with shows & movies across different time periods and countries.',
		},
	};
}

function getVideoUploadDate(title: TitleDetail) {
	let uploadDate = null;
	const minimumDate = new Date('2021-06-16');
	if (title.content?.originalReleaseDate) {
		uploadDate = new Date(title.content.originalReleaseDate);
		uploadDate.setMonth(uploadDate.getMonth() - 1);
	} else if (title.content.originalReleaseYear) {
		uploadDate = new Date(title.content.originalReleaseYear, 11, 31);
	}
	return uploadDate && uploadDate > minimumDate ? uploadDate.toISOString() : minimumDate.toISOString();
}

// list all available trailers in the following order:
// - videobuster
// - dailymotion
// - youtube
function getTrailers(title: TitleDetail, thumborFilter: ReturnType<typeof createThumborFilter>) {
	const uploadDate = getVideoUploadDate(title);
	const clips = [
		// videobuster
		...(title.content?.videobusterClips || []).map(clip => ({
			...clip,
			thumbnailUrl: undefined,
			contentUrl: clip.externalId,
		})),
		// dailymotion
		...(title.content?.dailymotionClips || []).map(clip => ({
			...clip,
			thumbnailUrl: undefined,
			contentUrl: `https://www.dailymotion.com/crawler/video/${clip.externalId}`,
		})),
		// youtube
		...(title.content.clips || []).map(clip => ({
			...clip,
			thumbnailUrl: `https://img.youtube.com/vi/${clip.externalId}/mqdefault.jpg`,
			contentUrl: `https://www.youtube.com/watch?v=${clip.externalId}`,
		})),
	];

	return clips.slice(0, 5).map(clip => ({
		...clip,
		'@type': 'VideoObject',
		name: (clip as any).name || title.content.title,
		description: title.content.shortDescription,
		uploadDate,
		// @note: when fullBackdrops is available in gql:
		// thumbnailUrl: clip.thumbnailUrl || (title.content.fullBackdrops || []).find(Boolean),
		thumbnailUrl:
			clip.thumbnailUrl ||
			(title.content?.backdrops || [])
				.map(el => thumborFilter({ url: el.backdropUrl.replace('{format}', 'jpg'), imageType: 'backdrop' }))
				.find(Boolean),
	}));
}

function getGenresForTitle(genresByShortName: Record<string, Genre>, title: TitleDetail) {
	if (Object.keys(genresByShortName).length) {
		return title.content.genres.map(genre => genresByShortName[genre.shortName]?.translation);
	}
	return [];
}

function getWatchActionsFromUpcomingOffers(title: TitleDetail, offers: UpcomingRelease[]) {
	// map upcoming release offers to WatchActions with future validFrom data
	return offers.map(offer => {
		const watchActionFromUpcomingOffers = {
			'@type': 'WatchAction',
			expectsAcceptanceOf: {
				'@type': 'Offer',
				availability: 'https://schema.org/NotYetAvailable',
				validFrom: offer.releaseDate,
				businessFunction: getUpcomingReleaseBusinessFunction(offer),
				...(hasViableProviderName(offer) && {
					offeredBy: {
						'@type': 'Organization',
						name: offer.package?.clearName,
					},
				}),
				...(isUpcomingReleaseWithPlanOffer(offer) && {
					priceCurrency: offer.package?.planOffers[0].currency,
					price: offer.package?.planOffers[0].retailPriceValue,
				}),
				...(showUpcomingOfferAdditionalProperties(title, offer) && {
					additionalProperty: [
						isUpcomingReleaseWithPlanOffer(offer) && {
							'@type': 'PropertyValue',
							name: 'BillingPeriod',
							value: 'Monthly',
						},
						title.content.runtime != null && {
							'@type': 'PropertyValue',
							name: 'duration',
							value: getDurationString(title.content.runtime ?? null),
						},
						getAgeRating(title) != null && {
							'@type': 'PropertyValue',
							name: 'contentRating',
							value: getAgeRating(title),
						},
					].filter(Boolean),
				}),
			},
		};

		return watchActionFromUpcomingOffers;
	});
}

function getWatchActionsFromOffers(
	title: TitleDetail,
	offers: TitleOfferFragment[],
	hideInternationalOffers: boolean = false
) {
	const languageStore = useLanguageStore();
	// map regular buybox offers to WatchActions
	return offers
		.filter(offer => !hideInternationalOffers || offer.country === languageStore.country)
		.map(offer => ({
			'@type': offer.monetizationType === MonetizationType.Cinema ? 'BuyAction' : 'WatchAction',
			target: {
				'@type': 'EntryPoint',
				urlTemplate: offer.standardWebURL,
			},
			expectsAcceptanceOf: {
				'@type': 'Offer',
				...(!!getPrice(offer) && { priceCurrency: offer.currency, price: getPrice(offer) }),
				availability: 'https://schema.org/InStock',
				businessFunction: getOfferBusinessFunction(offer),
				offeredBy: {
					'@type': 'Organization',
					name: offer.package.clearName,
				},
				// indicate an international offer by saying where it's allowed to consume it
				...(offer.country && offer.country !== languageStore.country
					? {
							areaServed: { '@type': 'Country', name: offer.country },
							eligibleRegion: { '@type': 'Country', name: offer.country },
					  }
					: {
							eligibleRegion: { '@type': 'Country', name: offer.country },
					  }),
				additionalProperty: [
					offer.monetizationType === MonetizationType.Flatrate &&
						!!getPrice(offer) && { '@type': 'PropertyValue', name: 'BillingPeriod', value: 'Monthly' },
					title.content.runtime != null && {
						'@type': 'PropertyValue',
						name: 'duration',
						value: getDurationString(title.content.runtime ?? null),
					},
					,
					!!getAgeRating(title) && {
						'@type': 'PropertyValue',
						name: 'contentRating',
						value: getAgeRating(title),
					},
					!!getVideoFormat(offer) && {
						'@type': 'PropertyValue',
						name: 'videoFormat',
						value: getVideoFormat(offer),
					},

					offer.audioLanguages.length && {
						'@type': 'PropertyValue',
						name: 'audioLanguage',
						value: offer.audioLanguages,
					},
					offer.audioTechnology.length && {
						'@type': 'PropertyValue',
						name: 'audioTechnology',
						value: offer.audioTechnology,
					},
					offer.videoTechnology.filter(el => el.trim().length).length && {
						'@type': 'PropertyValue',
						name: 'videoTechnology',
						value: offer.videoTechnology,
					},
					offer.subtitleLanguages.length && {
						'@type': 'PropertyValue',
						name: 'subtitleLanguages',
						value: offer.subtitleLanguages,
					},
				].filter(Boolean),
			},
		}));
}

function getPrice(offer: TitleOfferFragment) {
	if (offer.retailPriceValue) return offer.retailPriceValue;
	if (offer.package.planOffers.length) return offer.package.planOffers[0].retailPriceValue;
	return null;
}

function getAgeRating(title: TitleDetail) {
	if (title.__typename === 'Season') {
		return title.show.content.ageCertification;
	}
	return title.content.ageCertification || '';
}

function showUpcomingOfferAdditionalProperties(title: TitleDetail, offer: UpcomingRelease) {
	return !!title.content.runtime || !!getAgeRating(title) || isUpcomingReleaseWithPlanOffer(offer);
}

function isUpcomingReleaseWithPlanOffer(offer: UpcomingRelease) {
	return !!offer.package?.planOffers[0]?.retailPriceValue && !!offer.package?.planOffers[0]?.currency;
}

function hasViableProviderName(offer: UpcomingRelease) {
	return !!offer.package?.clearName && !['imd', 'tmd'].includes(offer.package.shortName);
}

function getVideoFormat(offer: TitleOfferFragment) {
	switch (offer.presentationType) {
		case PresentationType.Sd:
			return 'SD';
		case PresentationType.Hd:
			return 'HD';
		case PresentationType['4K']:
			return '4K';
		case PresentationType.Dvd:
			return 'DVD';
		case PresentationType.Bluray_4K:
		case PresentationType.Bluray:
			return 'Blu-Ray';

		default:
			return '';
	}
}

function getOfferBusinessFunction(offer: TitleOfferFragment) {
	if ([MonetizationType.Buy, MonetizationType.Cinema].includes(offer.monetizationType))
		return 'https://schema.org/SellAction';
	if (offer.monetizationType === MonetizationType.Rent) return 'https://schema.org/RentAction';
	if ([MonetizationType.Flatrate, MonetizationType.Free].includes(offer.monetizationType)) {
		return 'https://schema.org/ProvideService';
	}
	return 'https://schema.org/ProvideService';
}

function getUpcomingReleaseBusinessFunction(offer: UpcomingRelease) {
	if (offer.releaseType === 'THEATRICAL') return 'https://schema.org/SellAction';
	if (!offer.package?.monetizationTypes[0]) return 'https://schema.org/ProvideService';
	if ([MonetizationType.Buy, MonetizationType.Cinema].includes(offer.package?.monetizationTypes[0]))
		return 'https://schema.org/SellAction';
	if (offer.package?.monetizationTypes[0] === MonetizationType.Rent) return 'https://schema.org/RentAction';
	return 'https://schema.org/ProvideService';
}

function getCreditList(title: TitleDetail, type: string) {
	const field = type === CreditRole.Director ? 'directors' : 'credits';
	const credits = title.__typename === 'Season' ? title.show.content[field] : title.content[field];
	return credits.filter(credit => credit.role === type).map(credit => schemaPerson(credit.name));
}

function getActorList(title: TitleDetail) {
	const credits = title.__typename === 'Season' ? title.show.content.credits : title.content.credits;
	if (credits == null) return [];

	return credits
		.filter(credit => credit.role === CreditRole.Actor)
		.map(credit =>
			credit.characterName ? schemaPerformanceRole(credit.name, credit.characterName) : schemaPerson(credit.name)
		);
}

// function getCriticReviews(title: TitleDetail) {
// 	const criticReviews = title.content.textRecommendations?.filter(
// 		textRecommendation =>
// 			![ProfileType.JwEmployee, ProfileType.User].includes(textRecommendation.profile.profileType)
// 	);

// 	return criticReviews?.map(criticReview => {
// 		return {
// 			'@type': 'CriticReview',
// 			author: {
// 				'@type': 'Person',
// 				name: criticReview.profile.displayName,
// 			},
// 			publisher: {
// 				'@type': 'Organization',
// 				name: 'JustWatch',
// 			},
// 			datePublished: criticReview.updatedAt,
// 			headline: criticReview.headline,
// 			reviewBody: criticReview.body,
// 		};
// 	});
// }

// function getUserReviews(title: TitleDetail) {
// 	const userReviews = title.content.textRecommendations?.filter(textRecommendation =>
// 		[ProfileType.ProUser, ProfileType.User].includes(textRecommendation.profile.profileType)
// 	);

// 	return userReviews?.map(userReview => {
// 		return {
// 			'@type': 'UserReview',
// 			author: {
// 				'@type': 'Person',
// 				name: userReview.profile.displayName,
// 			},
// 			datePublished: userReview.updatedAt,
// 			headline: userReview.headline,
// 			reviewBody: userReview.body,
// 		};
// 	});
// }

// function getReviews(title: TitleDetail) {
// 	return [getCriticReviews(title), getUserReviews(title)].flat().filter(Boolean);
// }

function getTextRecommendations(title: TitleDetail) {
	const profileTypeMapping: Record<ProfileType, string> = {
		ACTOR: 'Actor',
		DIRECTOR: 'Director',
		INFLUENCER: 'Influencer',
		JW_EDITOR: 'Editor',
		JW_EMPLOYEE: 'Employee',
		PRODUCER: 'Producer',
		PRO_USER: 'User',
		USER: 'User',
		WRITER: 'Writer',
		VIP: 'VIP',
	};

	const textRecommendations = title.content.textRecommendations ?? [];

	return textRecommendations.map(textRecommendation => {
		return {
			'@type': 'Article',
			author: {
				'@type': 'Person',
				name: textRecommendation.profile.displayName,
				jobTitle:
					textRecommendation.customProfileType ?? textRecommendation.profile.profileType
						? profileTypeMapping[textRecommendation.profile.profileType]
						: 'Editor',
				sameAs: textRecommendation.profile.externalUrls?.length
					? textRecommendation.profile.externalUrls.map(({ url }) => url)
					: '',
			},
			publisher: {
				'@type': 'Organization',
				name: 'JustWatch',
			},
			datePublished: textRecommendation.updatedAt,
			headline: textRecommendation.headline,
			articleBody: textRecommendation.body,
			mainEntity: {
				'@type': title.objectType === ObjectType.Movie ? 'Movie' : 'TVSeries',
				'@id': new URL(title.content?.fullPath, APP_URL).toString(),
			},
		};
	});
}

function getDurationString(runtime: number | null) {
	if (runtime == null) return null;

	const hours = Math.floor(runtime / 60);
	const minutes = runtime % 60;

	// Time must be in ISO8601 format https://en.wikipedia.org/wiki/ISO_8601#Times
	// e.g.: PT1H39M0S
	return `PT${hours}H${minutes}M0S`;
}

function getSeasons(title: TitleDetail & { __typename: 'Show' }) {
	if (title.seasons == null || !title.content) return [];
	return title.seasons.map(season => {
		return {
			'@type': 'TVSeason',
			seasonNumber: season.content.seasonNumber,
			numberOfEpisodes: season.totalEpisodeCount,
			datePublished: season.content.originalReleaseYear,
			name: `${season.show?.content.title || ''} ${season.content.title}`,
			url: new URL(season.content.fullPath, APP_URL).toString(),
		};
	});
}

function schemaPerson(name: string) {
	return {
		'@type': 'Person',
		name,
	};
}

function schemaPerformanceRole(name: string, characterName: string) {
	return {
		'@type': 'PerformanceRole',
		characterName,
		actor: {
			'@type': 'Person',
			name,
		},
	};
}

const imgPathToUrl = (path?: string | null, profile = 's166') =>
	path ? path.replace('{profile}', profile).replace('{format}', 'jpeg') : '';

function _getArticleJsonLdContent({ content, author, mainObjects }: ArticleFragment, fullPath: string) {
	const headline = content?.title;
	const mainObject = mainObjects?.[0]?.node;
	type MainObject = AssertByTypename<typeof mainObject, 'GenericTitleList' | 'Movie' | 'Show' | 'Season'>;

	if (headline == null || mainObject == null) {
		import('@/helpers/sentry-helper').then(({ captureMessageForSentry }) => {
			captureMessageForSentry(
				'[json-ld-helper]: Article JSON LD missing headline or mainObject',
				{ where: '[json-ld-helper.ts] _getArticleJsonLdContent()' },
				'warning'
			);
		});
		return null;
	}

	const url = `https://www.${JW_CONFIG.DOMAIN}${fullPath}`;
	const languageStore = useLanguageStore();

	const jsonLdContent: Record<string, any> = {
		'@context': 'https://schema.org',
		'@type': mainObject.__typename === 'GenericTitleList' ? 'Article' : 'NewsArticle',
		inLanguage: languageStore.language,
		headline: headline[1],
		url,
		isBasedOn: url,
		image: {
			'@type': 'ImageObject',
			url: imgPathToUrl(getArticleBackdrop(mainObject as MainObject), 's480'),
			thumbnail: {
				'@type': 'ImageObject',
				url: imgPathToUrl(getArticleBackdrop(mainObject as MainObject), 's480'),
			},
		},
		datePublished: content.createdAt,
		dateModified: content.updatedAt,
		publisher: {
			'@type': 'NewsMediaOrganization',
			name: 'JustWatch',
			logo: {
				'@type': 'ImageObject',
				url: `${JW_CONFIG.IMAGESCALER_URL}/${ASSETS_DIR}/img/logo/JustWatch-logo-small.png`,
				width: 253,
				height: 38,
			},
		},
		audience: {
			'@type': 'Audience',
			name: 'Movie Fans',
		},
		mainEntity: [],
	};

	if (mainObject.__typename === 'GenericTitleList' && mainObject.titles.edges) {
		jsonLdContent.mainEntity = mainObject.titles.edges.map(({ node }) => ({
			'@type': getType(node.objectType),
			name: node.content.title,
			url: `https://www.${JW_CONFIG.DOMAIN}${node.content.fullPath}`,
			description: node.content.shortDescription,
			image: JW_CONFIG.IMAGESCALER_URL + imgPathToUrl(node.content.posterUrl),
			...getAggregateRating(node.content as MovieContent),
		}));
	} else {
		type NewsMainObject = AssertByTypename<typeof mainObject, 'Movie' | 'Show' | 'Season'>;
		const title = mainObject as NewsMainObject;

		jsonLdContent.mainEntity.push({
			'@type': getType(title.objectType),
			name: title.content.title,
			url: `https://www.${JW_CONFIG.DOMAIN}${title.content.fullPath}`,
			image: JW_CONFIG.IMAGESCALER_URL + imgPathToUrl(title.content.posterUrl),
			...getAggregateRating(title.content),
		});
	}

	return {
		'@graph': [jsonLdContent, _getAuthorJsonLdContent(author.content)],
	};
}

function _getAuthorJsonLdContent(author: AuthorDataFragment) {
	return {
		'@context': 'https://schema.org',
		'@type': 'Person',
		name: author.clearName,
		url: `https://www.${JW_CONFIG.DOMAIN}${author.fullPath}`,
		image: author.imageUrl,
		jobTitle: author.occupation?.length ? author.occupation.join(', ') : 'Official JustWatch Writer',
		sameAs: author.socialHandles?.length ? author.socialHandles.map(({ url }) => url) : undefined,
		reviewedBy: {
			'@type': 'Person',
			name: 'Samuel J Harries',
			jobTitle: 'Writer, Editor',
			email: 'samuel.james.harries@justwatch.com',
		},
		...(author.alumniOf?.length && author.alumniOf.some(el => el.length) && { alumniOf: author.alumniOf }),
		...(author.brand && { brand: author.brand }),
		knowsLanguage: author.languagesSpoken,
	};
}

function cleanHtmlToText(html: string): string {
	return (
		html
			// Remove extra quotes if present
			.replace(/^"|"$/g, '')
			// Remove HTML tags
			.replace(/<\/?[^>]+(>|$)/g, '')
			// Replace multiple spaces with a single space
			.replace(/\s+/g, ' ')
			.trim()
			// Remove or replace \n (if they appear as text) and return
			.replace(/\\n/g, ' ')
	);
}

function _getProfileJsonLdContent(profile: ProfileNode) {
	const affiliatedOrganizations = profile.externalUrls?.map(externalUrl => {
		return {
			'@type': 'Organization',
			name: externalUrl.name,
			url: externalUrl.url,
		};
	});

	return {
		'@context': 'https://schema.org',
		'@type': 'Person',
		name: profile.displayName,
		url: `https://www.${JW_CONFIG.DOMAIN}${profile.profileUrl}`,
		jobTitle: 'Official JustWatch Writer',
		image: profile.avatarUrl,
		description: cleanHtmlToText(profile.bio ?? ''),
		worksFor: {
			'@type': 'Organization',
			name: 'JustWatch',
			url: `https://www.${JW_CONFIG.DOMAIN}`,
		},
		affiliation: affiliatedOrganizations,
		sameAs: profile.externalUrls?.length ? profile.externalUrls.map(({ url }) => url) : undefined,
		reviewedBy: {
			'@type': 'Person',
			name: 'Samuel J Harries',
			jobTitle: 'Writer, Editor',
			email: 'samuel.james.harries@justwatch.com',
		},
	};
}

function _getPopularJsonLdContent(popularPageData: GetPopularTitlesQuery['popularTitles']) {
	const popularTitles = popularPageData?.edges ?? [];
	const titleAmount = popularPageData?.totalCount;
	const languageStore = useLanguageStore();

	const schemaListItems = popularTitles.map((title, index) => {
		let itemType = '';
		let itemAudience = '';
		if (title?.node?.objectType === ObjectType.Show) {
			itemType = 'TVSeries';
			itemAudience = 'TV Series Fans';
		}
		if (title?.node?.objectType === ObjectType.Movie) {
			itemType = 'Movie';
			itemAudience = 'Movie Fans';
		}

		// const genres = title?.node?.content?.genres?.map(genre => genre?.translation).join(', ') ?? '';

		const imageUrl = `https://www.justwatch.com/images${title?.node?.content?.posterUrl
			?.replace(/\{format\}/g, 'webp')
			.replace(/\{profile\}/g, 's718')}`;

		return {
			'@type': 'ListItem',
			position: index + 1,
			url: `https://www.justwatch.com${title?.node?.content?.fullPath}`,
			item: {
				'@type': itemType,
				name: title?.node?.content?.title ?? '',
				// added as unknown  to fix build error
				...getAggregateRating(title?.node?.content as unknown as MovieContent),
				// director: title?.node?.content?.credits?.[0]?.name ?? '',
				duration:
					title?.node?.objectType === ObjectType.Movie
						? getDurationString(title?.node?.content?.runtime || null) ?? ''
						: '',
				audience: {
					'@type': 'Audience',
					name: itemAudience,
				},
				// genre: genres,
				image: imageUrl,
			},
		};
	});

	return {
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		mainEntityOfPage: `List of the Most Popular Movies & TV Series in ${
			Country[languageStore.country.toUpperCase() as keyof typeof Country]
		}`,
		numberOfItems: titleAmount,
		itemListElement: schemaListItems,
	};
}

function _getNewJsonLdContent(newPageData: NewTitlesEdge[]) {
	const languageStore = useLanguageStore();

	const schemaListItems = newPageData.map((_, index: number) => {
		const titleNode = {} as Movie & Season;
		let titleContent = {} as MovieOrShowContent;
		let itemType = '';
		let itemAudience = '';
		if (titleNode.objectType === ObjectType.ShowSeason) {
			itemType = 'TVSeries';
			itemAudience = 'TV Series Fans';
			titleContent = titleNode?.show?.content as ShowContent;
		} else {
			itemType = 'Movie';
			itemAudience = 'Movie Fans';
			titleContent = titleNode.content as MovieContent;
		}

		const genres = titleContent?.genres?.map(genre => genre?.translation).join(', ') ?? '';

		const imageUrl = `https://www.justwatch.com/images${titleContent?.posterUrl
			?.replace(/\{format\}/g, 'webp')
			.replace(/\{profile\}/g, 's718')}`;

		return {
			'@type': 'ListItem',
			position: index + 1,
			url: `https://www.justwatch.com${titleContent?.fullPath}`,
			item: {
				'@type': itemType,
				name: titleContent?.title ?? '',
				description: titleContent?.shortDescription,
				...getAggregateRating(titleContent as MovieContent),
				duration:
					titleNode?.objectType === ObjectType.Movie
						? getDurationString(titleContent?.runtime || null) ?? ''
						: '',
				audience: {
					'@type': 'Audience',
					name: itemAudience,
				},
				genre: genres,
				image: imageUrl,
			},
		};
	});

	return {
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		mainEntityOfPage: `List of the newest Movies & TV Series in ${
			Country[languageStore.country.toUpperCase() as keyof typeof Country]
		}`,
		numberOfItems: newPageData.length,
		itemListElement: schemaListItems,
	};
}

function withSchemaErrorHandling<T extends (...a: any[]) => any>(fn: T) {
	return (...args: Parameters<T>) => {
		try {
			return fn(...args);
		} catch (error: any) {
			import('@/helpers/sentry-helper').then(({ captureMessageForSentry }) => {
				captureMessageForSentry(
					`[json-ld-helper]: ${fn.name} failed`,
					{ where: '[json-ld-helper.ts]', error },
					'error'
				);
			});
			return {};
		}
	};
}

export const getJsonLdContent = withSchemaErrorHandling(_getJsonLdContent);
export const getArticleJsonLdContent = withSchemaErrorHandling(_getArticleJsonLdContent);
export const getAuthorJsonLdContent = withSchemaErrorHandling(_getAuthorJsonLdContent);
export const getProfileJsonLdContent = withSchemaErrorHandling(_getProfileJsonLdContent);
export const getPopularJsonLdContent = withSchemaErrorHandling(_getPopularJsonLdContent);
export const getNewJsonLdContent = withSchemaErrorHandling(_getNewJsonLdContent);
